import { Badge, Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
    WORKFLOW_STEPS,
    WorkflowStep,
} from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import useExportableItems from '~/hooks/export/useExportableItems';
import useWorkflowSteps from './useWorkflowSteps';

interface StepIndicatorProps {
    step: WorkflowStep;
    label: string;
    isActive: boolean;
    isDisabled: boolean;
    disabledReason?: string;
    badgeContent?: number;
    onClick: () => void;
}

function StepIndicator({
    step,
    label,
    isActive,
    isDisabled,
    disabledReason,
    badgeContent,
    onClick,
}: StepIndicatorProps) {
    const Icon = step.icon;

    return (
        <Tooltip
            title={
                <Box>
                    <Typography fontSize={11}>{label}</Typography>
                    <Typography fontSize={10} variant="body2">
                        {disabledReason}
                    </Typography>
                </Box>
            }
            placement="right"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Badge
                    badgeContent={badgeContent}
                    color="primary"
                    overlap="circular"
                    sx={(theme) => ({
                        '& .MuiBadge-badge': {
                            fontSize: '0.6rem',
                            minWidth: 16,
                            height: 16,
                            ...(isActive && {
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.main,
                            }),
                        },
                    })}
                >
                    <ButtonBase
                        data-testid={`${step.id}-nav-button`}
                        onClick={isDisabled ? undefined : onClick}
                        sx={(theme) => ({
                            width: 42,
                            height: 42,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isActive
                                ? theme.palette.primary.main
                                : 'transparent',
                            border: `1px solid ${
                                isActive
                                    ? theme.palette.primary.main
                                    : theme.palette.divider
                            }`,
                            color: isActive
                                ? theme.palette.common.white
                                : theme.palette.text.secondary,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: isActive
                                    ? theme.palette.primary.main
                                    : theme.palette.action.hover,
                            },
                            ...(isDisabled && {
                                opacity: 0.35,
                                cursor: 'default',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }),
                        })}
                    >
                        <Icon size={15} />
                    </ButtonBase>
                </Badge>
            </Box>
        </Tooltip>
    );
}

export default function WorkflowStepper() {
    const navigate = useNavigate();
    const intl = useIntl();
    const { sessionId } = useParams<{ sessionId?: string }>();
    const { currentStepIndex, availability } = useWorkflowSteps();
    const { notExportedCount } = useExportableItems();

    const handleStepClick = (step: WorkflowStep) => {
        const path = sessionId
            ? `/session/${sessionId}${step.path}`
            : step.path;
        navigate(path);
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            height="100%"
            px={0.5}
            ml={0.5}
            bgcolor="background.paper"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 0.5,
                    borderRadius: 1,
                }}
            >
                {WORKFLOW_STEPS.map((step, index) => {
                    const stepAvailability = availability[step.id];
                    const isDisabled = stepAvailability?.disabled ?? false;
                    const disabledReason = stepAvailability?.disabledReasonId
                        ? intl.formatMessage({
                              id: stepAvailability.disabledReasonId,
                          })
                        : undefined;

                    return (
                        <StepIndicator
                            key={step.id}
                            step={step}
                            label={intl.formatMessage({
                                id: step.labelId,
                            })}
                            isActive={index === currentStepIndex}
                            isDisabled={isDisabled}
                            disabledReason={disabledReason}
                            badgeContent={
                                step.id === 'export'
                                    ? notExportedCount
                                    : undefined
                            }
                            onClick={() => handleStepClick(step)}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}
